import React, { useContext, useState, useMemo } from "react";
import { Box, Tab, Tabs, CircularProgress } from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import PeopleIcon from "@mui/icons-material/People";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";

import ExhibitorFavorites from "./ExhibitorFavorites";
import ProductFavorites from "./ProductFavorites";
import VisitorFavorites from "./VisitorFavorites";
import BackBtn from "../../../widgets/BackBtn";

import BookmarkIcon from "@mui/icons-material/Bookmark";
import PageHeader from "../common/PageHeader";
import { UserContext } from "../../context/UserContext";
import { USER_ROLES } from "../../../constants/constants";

// 1. Extract repetitive styles outside the component
const tabStyles = {
  textTransform: "none",
  fontSize: { xs: "14px", sm: "16px" },
  fontWeight: 600,
  color: "#64748b",
  px: 3,
  py: 2,
  "&.Mui-selected": { color: "#0f172a", fontWeight: 800 },
  "&:hover": { color: "#2563eb" },
};

const MyFavorites = () => {
  // 2. Use string identifiers instead of index numbers (0, 1, 2)
  const [activeTab, setActiveTab] = useState("exhibitors");
  const { user, loading } = useContext(UserContext);

  const myRole = user?.role;

  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // 3. Create a dynamic configuration array for tabs based on role
  const tabs = useMemo(() => {
    const baseTabs = [
      {
        value: "exhibitors",
        label: "Exhibitors",
        icon: <BusinessIcon />,
        component: <ExhibitorFavorites />,
      },
    ];

    if (myRole === USER_ROLES.EXHIBITOR) {
      baseTabs.push({
        value: "visitors",
        label: "Visitors",
        icon: <PeopleIcon />,
        component: <VisitorFavorites />,
      });
    }

    baseTabs.push({
      value: "products",
      label: "Products",
      icon: <ShoppingCartIcon />,
      component: <ProductFavorites />,
    });

    return baseTabs;
  }, [myRole]);

  // 4. Handle the unused loading state to prevent layout shifts
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        backgroundColor: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      <BackBtn />

      <PageHeader
        title="My Bookmarks"
        subtitle="Quick access to your saved exhibitors, products, and delegates"
        icon={BookmarkIcon}
      />

      <Tabs
        value={activeTab}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mt: 2,
          mb: 4,
          borderBottom: "1px solid #e2e8f0",
          "& .MuiTabs-indicator": {
            height: 3,
            borderRadius: "3px 3px 0 0",
            backgroundColor: "#2563eb",
          },
        }}
      >
        {/* Render tabs dynamically from the config */}
        {tabs.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value} // Binds to our explicit string
            icon={tab.icon}
            iconPosition="start"
            label={tab.label}
            sx={tabStyles}
          />
        ))}
      </Tabs>

      <Box>
        {/* 5. Clean, bug-free rendering logic */}
        {tabs.find((tab) => tab.value === activeTab)?.component}
      </Box>
    </Box>
  );
};

export default MyFavorites;
